import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { permalinkForSocialPost } from "@/lib/permalinks";
import { markdownToPlain } from "@/lib/markdownPlain";
import { cn } from "@/lib/utils";

/**
 * Post embed in social DMs. Always uses a solid readable surface so text stays
 * legible inside outgoing (primary) bubbles — same idea as workplace DmPostAttachmentCard.
 */
export default function SocialDmPostCard({
  post,
  inverse = false,
  compact = false,
}) {
  if (!post) return null;

  const shell = cn(
    "block rounded-xl border overflow-hidden text-left transition-colors no-underline",
    inverse
      ? "border-black/10 bg-white/90 text-[hsl(24_30%_12%)] shadow-sm hover:bg-white"
      : "border-border bg-background text-foreground hover:bg-muted/40",
    compact ? "min-w-[200px]" : "min-w-[240px] max-w-sm"
  );

  if (post.unavailable) {
    return (
      <div className={cn(shell, "pointer-events-none")}>
        <div
          className={cn(
            "px-3 py-2",
            compact ? "space-y-0.5" : "space-y-1"
          )}
        >
          <p
            className={cn(
              "font-medium",
              inverse ? "text-black/55" : "text-muted-foreground",
              compact ? "text-xs" : "text-sm"
            )}
          >
            Content unavailable
          </p>
          <p
            className={cn(
              "text-xs",
              inverse ? "text-black/45" : "text-muted-foreground"
            )}
          >
            You don’t have access to this post.
          </p>
        </div>
      </div>
    );
  }

  const postId = post.social_post_id ?? post.post_id ?? post.postId;
  const href = permalinkForSocialPost({ postId });
  const title =
    markdownToPlain(post.title || "") ||
    markdownToPlain(post.content || "").slice(0, 80) ||
    `Post #${postId}`;
  const excerpt = markdownToPlain(post.content || "").slice(
    0,
    compact ? 100 : 180
  );

  return (
    <Link
      to={href || "#"}
      onClick={(e) => e.stopPropagation()}
      className={shell}
    >
      <div className={cn("px-3 py-2", compact ? "space-y-0.5" : "space-y-1")}>
        <div className="flex items-start justify-between gap-2">
          <p
            className={cn(
              "font-medium line-clamp-2",
              inverse ? "text-[hsl(24_30%_12%)]" : "text-foreground",
              compact ? "text-xs" : "text-sm"
            )}
          >
            {title}
          </p>
          <ExternalLink
            className={cn(
              "h-3.5 w-3.5 shrink-0 mt-0.5",
              inverse ? "text-black/40" : "text-muted-foreground"
            )}
          />
        </div>
        {excerpt ? (
          <p
            className={cn(
              "text-xs line-clamp-2",
              inverse ? "text-black/55" : "text-muted-foreground"
            )}
          >
            {excerpt}
          </p>
        ) : null}
      </div>
    </Link>
  );
}
